import { SocketService } from '@/service/SocketsService'
import { UsersService } from '@/service/UsersService'

import { i18n } from '@/main'
import { MemberStatus } from '@/models/Groups'
import { TimeFrame } from '@/models/Leaderboard'
import { IUser } from '@/models/Users'
import { GokollabService } from '@/service/GokollabService'
import restAgent from '@/service/restAgent'
import { useGlobalStore } from '@/store/app'
import { reactive } from 'vue'
import { app } from './app'
import { ConversationState } from './conversations'
import { GroupState } from './groups'
import { Theme } from './themes'

export class UsersState {
  state = reactive({
    processing: false,
    isCurrentUserFetching: false,
    currentUser: undefined as IUser | undefined,
    usersData: new Map<string, IUser>(),
    showMandatoryInfoModal: false,
  })

  reset(): void {
    this.state.processing = false
    this.state.usersData = new Map<string, IUser>()
    if (this.state.currentUser)
      this.state.currentUser.gamificationMeta = undefined
  }

  async getCurrentUser(): Promise<IUser | undefined> {
    try {
      if (this.state.currentUser) {
        return this.state.currentUser
      }
      const locationId = useGlobalStore().$state.locationId
      const id = useGlobalStore().$state.userId
      const response = await restAgent.ClientPortal.getUserDetails(
        locationId,
        id
      )
      // const contactLocationId = response.data.contactLocationId
      // const gokollabContactId = await this.getGokollabContactId(
      //   contactLocationId,
      //   id
      // )
      this.state.currentUser = { ...response.data }
      i18n.global.locale.value =
        response?.data?.languagePreference ||
        useGlobalStore().$state.languagePreferenceBuilder ||
        'en'
      Theme.state.isDarkMode = this.state.currentUser?.darkMode ?? false

      // if the user is fetched successfully, it means the user is authenticated we can now initiate socket connection
      SocketService.initializeSocketService(
        ConversationState.handleSocketMessage,
        ConversationState.handleSocketConnectionState
      )

      // useGlobalStore().setProfileDetails(this.state.currentUser)
      return this.state.currentUser
    } catch (error) {
      console.error(error)
    }
  }
  async fetchCurrentUserRankData(timeframe?: TimeFrame): Promise<void> {
    try {
      if (this.state.currentUser?.gamificationMeta?.leaderboard) {
        if (
          timeframe &&
          this.state.currentUser.gamificationMeta.leaderboard[timeframe]
        ) {
          return
        } else if (
          !timeframe &&
          this.state.currentUser.gamificationMeta.leaderboard.size > 0
        ) {
          return
        } else {
          const userRanks =
            await restAgent.ClientPortal.getUserRankOnLeaderboard(
              useGlobalStore().$state.groupId,
              useGlobalStore().$state.userId,
              timeframe
            )
          userRanks.data.forEach(userRank => {
            // Access the data property
            this.state.currentUser!.gamificationMeta!.leaderboard!.set(
              userRank.timeFrame,
              userRank.rank
            )
          })
          return
        }
      }
      const userRanks = await restAgent.ClientPortal.getUserRankOnLeaderboard(
        useGlobalStore().$state.groupId,
        useGlobalStore().$state.userId,
        timeframe
      )
      const gamificationMeta = {
        leaderboard: new Map<TimeFrame, number>(),
      }

      this.state.currentUser!.gamificationMeta = gamificationMeta
      userRanks.data.forEach(userRank => {
        // Access the data property
        this.state.currentUser!.gamificationMeta!.leaderboard!.set(
          userRank.timeFrame,
          userRank.rank
        )
      })
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  async getUserByContactId(contactId: string): Promise<IUser | undefined> {
    try {
      if (!contactId) {
        return undefined
      }

      if (this.state.usersData.has(contactId)) {
        return this.state.usersData.get(contactId)
      }
      const user = await UsersService.getUserDataBycontactId(contactId)

      if (!user.gamificationMeta) {
        user.gamificationMeta = {
          points: 0,
          level: 1,
        }
      }
      this.state.usersData.set(contactId, user)
      return this.state.usersData.get(contactId)
    } catch (error) {
      console.error(error)
      throw error
    }
  }

  async getGokollabContactId(
    locationId: string,
    contactId: string
  ): Promise<string | null> {
    try {
      const response = await GokollabService.getGokollabContactId(
        locationId,
        contactId
      )
      return response.contactId
    } catch (error) {
      console.error(error)
      return null
    }
  }
  async handleRemoveOrDeclineRequest(
    contactId: string,
    _status: MemberStatus,
    declining = false
  ): Promise<void> {
    await UsersService.removeUser(contactId)
    GroupState.state.groupData.membersCount -= 1
    if (GroupState.state.requestCount > 0 && declining)
      GroupState.state.requestCount -= 1
  }
  async handleApproveRequest(contactId: string): Promise<void> {
    await UsersService.updateUserGroup({
      contactId: contactId,
      status: MemberStatus.ACTIVE,
    })
    GroupState.state.groupData.membersCount += 1
    if (GroupState.state.requestCount > 0) GroupState.state.requestCount -= 1
  }
  async updateUserInfo(data: any): Promise<void> {
    try {
      const locationId = useGlobalStore().$state.locationId
      const id = useGlobalStore().$state.userId

      const response = await restAgent.ClientPortal.updateUserDetails(
        locationId,
        id,
        data
      )
      const contactLocationId = response.data.contactLocationId
      const gokollabContactId = await this.getGokollabContactId(
        contactLocationId,
        id
      )
      this.state.currentUser = { ...response.data, gokollabContactId }
      Theme.state.isDarkMode = this.state.currentUser?.darkMode ?? false

      // useGlobalStore().setProfileDetails(this.state.currentUser)
    } catch (error) {
      console.error(error)
    }
  }

  checkMandatedInfoPresentOrNot(): boolean {
    if (app.state.fetching || !this.state.currentUser) {
      return true
    }
    return this.state.currentUser?.avatar !== undefined
  }

  setMandatoryInfoModal(value: boolean): void {
    this.state.showMandatoryInfoModal = value
  }
}

export const UserState = new UsersState()
